/**
 * Canonical TanStack Query keys shared across components.
 *
 * A new (or deleted) site has to show up app-wide, not just on the page that
 * mutated it: the topbar project picker (`ProjectSelector`) and the portfolio
 * (`PortfolioPage`) read SEPARATE queries from the sync page. Centralising the
 * keys here keeps the cross-component invalidations in lockstep — a key that
 * drifts between writer and reader silently breaks the refetch.
 *
 * AuthContext seeds the projects list off the login/refresh response; it
 * imports `PROJECTS_LIST_KEY` so the seeded cache and the picker's query share
 * one identity.
 */
import type { HelpLanguage } from '@apis/help';

/** Projects list — seeded by AuthContext, read by the topbar ProjectSelector. */
export const PROJECTS_LIST_KEY = ['projects', 'list'] as const;

/** Portfolio dashboard — read by PortfolioPage. */
export const PORTFOLIO_KEY = ['portfolio'] as const;

/**
 * Portfolio keyed by the active site + its selected schedule, so switching the
 * TopBar picker is a distinct query (a clean refetch of the new pin). Prefixed
 * by `PORTFOLIO_KEY`, so coarse `invalidateQueries({ queryKey: PORTFOLIO_KEY })`
 * still matches every pinned variant.
 */
export const portfolioKey = (siteId: string | undefined, scheduleNumber: string | undefined) =>
  [...PORTFOLIO_KEY, siteId ?? null, scheduleNumber ?? null] as const;

/** A site's schedule list — drives the scheduler picker. */
export const scheduleListKey = (siteId: string) => ['schedule', 'list', siteId] as const;

/**
 * One parsed schedule (Gantt) for a site + schedule number. A `null` slot marks
 * "no schedule selected yet" — distinct from any real scheduleNumber — so the
 * disabled-query key can't silently collide with a loaded schedule's cache slot.
 */
export const scheduleKey = (siteId: string, scheduleNumber: string | undefined) =>
  ['schedule', 'detail', siteId, scheduleNumber ?? null] as const;

/**
 * A site's COMMITTED activities — the Activities & Issues page's feed (the
 * overlay collection joined with Trusstor's group/area status). Site-scoped,
 * not schedule-scoped: an activity is a promise made from a site's plan, and
 * the page shows the site's promises whatever schedule they came from.
 *
 * Every per-task execution write invalidates it (see `useImmediateWritePersist`),
 * and so does any frame on the activities SSE stream.
 */
export const committedActivitiesKey = (siteId: string) => ['activities', 'committed', siteId] as const;

/** The checklist templates a site may apply (read-mostly; Genba's). */
export const checklistTemplatesKey = (siteId: string) => ['checklists', 'templates', siteId] as const;

/** Who a commit may be assigned to — the Trusstor project's users. */
export const commitAssigneesKey = (siteId: string) => ['activities', 'assignees', siteId] as const;

/** A site's area catalog (floors → zones) — read by the Scheduler Locations tab. */
export const siteLocationsKey = (siteId: string) => ['site', 'locations', siteId] as const;

/** A site's CAD take-off (per-storey material quantities) — read by the
 *  Scheduler "stock" panel. */
export const cadTakeoffKey = (siteId: string) => ['cad', 'takeoff', siteId] as const;

/** A site's live profession catalog — read by the Scheduler task-assignee picker. */
// 'analytics' disambiguates from the Issues-page catalog query
// (['site', 'professions', siteId] → /api/sites/:id/professions, object-shaped):
// the two endpoints return different shapes and must never share a cache slot.
export const siteProfessionsKey = (siteId: string) => ['site', 'professions', 'analytics', siteId] as const;

/**
 * Weather-day queries (the heavy AI body). The bare prefix exists for
 * invalidation: schedule writes (save, progress/status) change which
 * activities a forecast day carries, so writers invalidate every variant.
 * The backend insights cache keys on the per-day activity set, so a refetch
 * after a weather-irrelevant edit is a cache hit — no extra AI call.
 */
export const WEATHER_DAY_KEY = ['weather', 'day'] as const;

/**
 * One weather day body for a site + schedule pin + ribbon day. `scheduleNumber`
 * is the user's RAW pin (`useSelectedScheduleNumber`) — null means "no explicit
 * choice", letting the backend resolve the latest-updated schedule; a stale pin
 * degrades server-side the same way, mirroring `portfolioKey`.
 */
export const weatherDayKey = (
  siteId: string | undefined,
  scheduleNumber: string | null,
  date: string | undefined,
) => [...WEATHER_DAY_KEY, siteId, scheduleNumber ?? null, date ?? 'default'] as const;

/** The cheap forecast ribbon — weather-only; schedule writes never touch it. */
export const weatherForecastKey = (siteId: string | undefined) =>
  ['weather', 'forecast', siteId] as const;

/** The root every help query hangs under; invalidating it refreshes the lot, in every language. */
export const helpRootKey = ['help'] as const;
export const helpGuidesKey = (language: HelpLanguage) => ['help', 'guides', language] as const;
export const helpGuideKey = (slug: string, language: HelpLanguage) => ['help', 'guide', slug, language] as const;
export const helpSearchIndexKey = (language: HelpLanguage) => ['help', 'search-index', language] as const;
